/**
 * MCP server management store — CRUD, tool discovery, and connection testing.
 */

import { create } from "zustand";
import type { McpServer, McpServersResponse, McpTool, McpbInstallResponse, McpbManifest } from "@/api/types";
import { apiFetch } from "@/api/client";

interface McpState {
  servers: McpServer[];
  isLoading: boolean;

  /** Load all MCP servers (built-in + user-added) */
  loadServers: () => Promise<void>;

  /** Add a new user MCP server */
  addServer: (name: string, transport: string, config: Record<string, unknown>) => Promise<McpServer>;

  /** Update a user MCP server */
  updateServer: (id: string, updates: {
    name?: string;
    transport?: string;
    config?: Record<string, unknown>;
    enabled?: boolean;
  }) => Promise<void>;

  /** Delete a user MCP server */
  deleteServer: (id: string) => Promise<void>;

  /** Discover tools from a server */
  discoverTools: (id: string) => Promise<{ success: boolean; tools: McpTool[]; error?: string }>;

  /** Toggle a single tool on/off */
  toggleTool: (serverId: string, toolName: string, enabled: boolean) => Promise<void>;

  /** Test connection to a server */
  verifyServer: (id: string) => Promise<{ verified: boolean; message: string }>;

  /** Restart a managed server process */
  restartServer: (id: string) => Promise<{ ok: boolean; status: string; message?: string }>;

  /** Get MCPB config (manifest + current values) */
  getMcpbConfig: (id: string) => Promise<{ manifest: McpbManifest; user_config_values: Record<string, unknown> }>;

  /** Update MCPB user_config values and restart */
  updateMcpbConfig: (id: string, values: Record<string, unknown>) => Promise<{ ok: boolean; restarted: boolean }>;

  /** Parse an MCPB file and return the manifest */
  installMcpb: (filePath: string) => Promise<McpbInstallResponse>;

  /** Confirm MCPB installation with user config values */
  confirmMcpbInstall: (tempDir: string, userConfigValues: Record<string, unknown>) => Promise<McpServer>;
}

export const useMcpStore = create<McpState>((set, get) => ({
  servers: [],
  isLoading: false,

  loadServers: async () => {
    set({ isLoading: true });
    try {
      const res = await apiFetch<McpServersResponse>("/mcp/servers");
      set({ servers: res.servers, isLoading: false });
    } catch (e) {
      console.error("Failed to load MCP servers:", e);
      set({ isLoading: false });
    }
  },

  addServer: async (name, transport, config) => {
    const server = await apiFetch<McpServer>("/mcp/servers", {
      method: "POST",
      body: JSON.stringify({ name, transport, config }),
    });
    set({ servers: [...get().servers, server] });
    return server;
  },

  updateServer: async (id, updates) => {
    await apiFetch(`/mcp/servers/${id}`, {
      method: "PUT",
      body: JSON.stringify(updates),
    });
    await get().loadServers();
  },

  deleteServer: async (id) => {
    await apiFetch(`/mcp/servers/${id}`, { method: "DELETE" });
    set({ servers: get().servers.filter((s) => s.id !== id) });
  },

  discoverTools: async (id) => {
    const result = await apiFetch<{ success: boolean; tools: McpTool[]; error?: string }>(
      `/mcp/servers/${id}/discover`,
      { method: "POST" },
    );
    // Reload servers to get updated tool lists
    await get().loadServers();
    return result;
  },

  toggleTool: async (serverId, toolName, enabled) => {
    await apiFetch(`/mcp/servers/${serverId}/tools/${toolName}`, {
      method: "PUT",
      body: JSON.stringify({ enabled }),
    });
    // Update local state
    set({
      servers: get().servers.map((s) =>
        s.id === serverId
          ? {
              ...s,
              tools: s.tools.map((t) =>
                t.name === toolName ? { ...t, enabled } : t
              ),
            }
          : s
      ),
    });
  },

  verifyServer: async (id) => {
    const result = await apiFetch<{ verified: boolean; message: string }>(
      `/mcp/servers/${id}/verify`,
      { method: "POST" },
    );
    // Reload to get updated status
    await get().loadServers();
    return result;
  },

  restartServer: async (id) => {
    const result = await apiFetch<{ ok: boolean; status: string; message?: string }>(
      `/mcp/servers/${id}/restart`,
      { method: "POST" },
    );
    await get().loadServers();
    return result;
  },

  getMcpbConfig: async (id) => {
    return apiFetch<{ manifest: McpbManifest; user_config_values: Record<string, unknown> }>(
      `/mcp/servers/${id}/config`,
    );
  },

  updateMcpbConfig: async (id, values) => {
    const result = await apiFetch<{ ok: boolean; restarted: boolean }>(
      `/mcp/servers/${id}/config`,
      { method: "PUT", body: JSON.stringify({ user_config_values: values }) },
    );
    await get().loadServers();
    return result;
  },

  installMcpb: async (filePath) => {
    return apiFetch<McpbInstallResponse>("/mcp/servers/install", {
      method: "POST",
      body: JSON.stringify({ file_path: filePath }),
    });
  },

  confirmMcpbInstall: async (tempDir, userConfigValues) => {
    const server = await apiFetch<McpServer>("/mcp/servers/install/confirm", {
      method: "POST",
      body: JSON.stringify({ temp_dir: tempDir, user_config_values: userConfigValues }),
    });
    await get().loadServers();
    return server;
  },
}));
